import {
    Bundle,
    ClientSDK,
    Product,
    RegistrationUpdateRequestBody,
    RenewalPeriod,
    Tier,
} from '@easeltv/client-sdk';
import { error } from '@easeltv/logger';
import { ApiFail, ResponseStatusInvalidFail } from '@easeltv/remote';
import { is } from '@easeltv/utilities';

import {
    resolveBundle,
    resolveTier,
} from '../../../hooks/useSubscription/useSubscription';

/* -------------------------------- Constants ------------------------------- */

const UrlToWantsToPurchaseRegex = /^\/(product|tier|bundle)\/([A-Za-z0-9-]+)/;

/* --------------------------------- Helpers -------------------------------- */

const isTier = (tier: unknown): tier is Tier => Tier.safeParse(tier).success;
const isBundle = (tier: unknown): tier is Bundle =>
    Bundle.safeParse(tier).success;

export const isBasket = (basket: unknown): basket is (Tier | Bundle)[] =>
    is.array(basket) && basket.every((item) => isTier(item) || isBundle(item));

type WantsToData = Pick<
    RegistrationUpdateRequestBody,
    'wantsToPurchase' | 'wantsToSubscribe' | 'wantsToSubscribeInterval'
>;

/**
 * mapWantsToData
 *
 * Maps a location state to a wantsToPurchase or wantsToSubscribe object
 *
 * @param api - Client SDK instance
 * @param url - URL to resolve
 * @param basket - Basket of subscription items
 * @param interval - Subscription interval
 * @returns
 */
export const mapWantsToData = async (
    api: ClientSDK,
    url?: string,
    basket?: unknown,
    interval?: string,
): Promise<WantsToData> => {
    if (isBasket(basket) && basket.length > 0) {
        return {
            wantsToSubscribe: basket.map((item) => `${item.type}_${item.id}`),
            wantsToSubscribeInterval: interval,
        };
    }

    if (!url) return {};

    const match = UrlToWantsToPurchaseRegex.exec(url);

    if (!match) return {};

    const [, type, ref] = match;

    const result = await api.getProductByReference(ref);

    if (!result.ok) {
        error(result.payload);

        return {};
    }

    const response = result.payload;

    // Match is an iterable, with the 0th index begin the whole matched regex, 1st and 2nd indexes are the values of the
    // two capture groups - which we return to create the namespaced id
    // ie. product_d904ad11-eb11-4b09-8186-ae98b12bd428
    return {
        wantsToPurchase: `${type}_${response.body.id}`,
    };
};

type WantsToPurchase =
    | { type: 'product'; item: Product }
    | { type: undefined; item: undefined };

export const resolveWantsToPurchase = async (
    itemId: string,
    api: ClientSDK,
): Promise<WantsToPurchase> => {
    const [type, id] = itemId.split('_');

    if (type === 'product') {
        const result = await api.getProduct(id);

        if (!result.ok) {
            error(result.payload);

            return {
                type: undefined,
                item: undefined,
            } as const;
        }

        return {
            type: 'product',
            item: result.payload.body,
        } as const;
    }

    return {
        type: undefined,
        item: undefined,
    } as const;
};

export const resolveWantsToSubscribe = async (
    api: ClientSDK,
    wantsToSubscribe: string[] = [],
    wantsToSubscribeInterval = '',
) => {
    const subscriptionResult = await api.getSubscription({
        extend: ['price-band'],
    });

    const intervalResult = RenewalPeriod.safeParse(wantsToSubscribeInterval);

    if (!subscriptionResult.ok || !intervalResult.success) {
        error({ subscriptionResult, intervalResult });

        return undefined;
    }

    const tiers = subscriptionResult.payload.body.tiers
        .map((tier) =>
            resolveTier(
                tier,
                subscriptionResult.payload.body.extend?.priceBands ?? [],
                [],
            ),
        )
        .filter(is.notUndefined);
    const bundles = subscriptionResult.payload.body.bundles
        .map((bundle) =>
            resolveBundle(
                bundle,
                subscriptionResult.payload.body.extend?.priceBands ?? [],
                [],
            ),
        )
        .filter(is.notUndefined);

    const renewalPeriod = intervalResult.data;

    const basket = wantsToSubscribe
        .map((item) => {
            const [type, id] = item.split('_');

            if (type === 'tier') {
                const tier = tiers.find((t) => t.id === id);

                if (tier && tier.priceBands[renewalPeriod]) {
                    return tier;
                }
            }

            if (type === 'bundle') {
                const bundle = bundles.find((b) => b.id === id);

                if (bundle && bundle.priceBands[renewalPeriod]) {
                    return bundle;
                }
            }

            return undefined;
        })
        .filter(is.notUndefined);

    return {
        basket,
        renewalPeriod,
    };
};

/**
 * handleRegistrationError
 *
 * Parses the returned SDK error and returns readable error text to display at the top of the registration form
 *
 * @param response Error response from the SDK
 * @returns An object of errors with the key being the id of the field
 */
export const handleRegistrationError = (
    response: ApiFail,
): [string, string[]][] => {
    if (response instanceof ResponseStatusInvalidFail) {
        if (response.key === 'error.registration.email-already-registered') {
            return [['email', ['Email has already been registered']]];
        }
        if (response.key === 'error.user.unauthorized') {
            return [['*', ['login.error.invalid']]];
        }
    }

    error(response);

    return [['*', ['An unknown error has occurred.']]];
};
